/**
 * @lines98/client/lib/idempotency-key — generate a stable per-ACTION idempotency
 * nonce for mutations whose server side has NO DB-natural idempotency (forge-gem).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (#21 forge-gem hole). Сплав «60 осколков → 1 камень» —
 * by-design ПОВТОРЯЕМАЯ операция (один цвет сплавляется много раз), поэтому сервер не
 * может дедупить её по натур-ключу. Защита от двойного списания/выдачи при сетевом
 * ретрае = ADR-0005 idempotency-ключ: клиент шлёт стабильный nonce per-ДЕЙСТВИЕ
 * заголовком `X-Idempotency-Key`, сервер чтит первый результат на этот ключ.
 *
 * **Stable per-action, NOT per-retry.** Один логический сплав (одно подтверждение
 * игрока → один вызов `forgeGem`) = один nonce. Внутренний ретрай транспорта
 * (`api-client` re-mint после stale-identity 401) переиспользует тот же header →
 * сервер видит тот же ключ → эффект один. Новое НАМЕРЕННОЕ действие (игрок снова тапает
 * после явной ошибки) = новый вызов = новый nonce — это by-design отдельный сплав.
 *
 * Nonce — непрозрачная для сервера случайная строка (сервер хеширует её вместе с
 * `player_id` сессии — `hash/mutation-idempotency-key.ts`); коллизии практически
 * исключены (122-bit UUIDv4 / 128-bit random fallback).
 *
 * Cross-references:
 *   - ADR-0005 §«Options Considered» Option B (client nonce + server player_id).
 *   - services/meta-api.ts `forgeGem` (потребитель — шлёт ключ заголовком).
 *   - packages/server/src/hash/mutation-idempotency-key.ts (серверная деривация ключа).
 */

/**
 * Mint a fresh idempotency nonce for ONE action. Uses the platform `crypto.randomUUID`
 * when available (all evergreen browsers + secure contexts); falls back to a
 * `getRandomValues`-seeded hex string on the rare runtime that lacks it. Never throws —
 * a mutation must not be blocked by a missing crypto primitive.
 *
 * @returns an opaque random string safe to send as `X-Idempotency-Key`.
 */
export function mintIdempotencyKey(): string {
  const c: Crypto | undefined =
    typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  // Fallback: 16 random bytes → hex (128 bits of entropy, same collision-safety class).
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = c.getRandomValues(new Uint8Array(16));
    let hex = '';
    for (const b of bytes) hex += b.toString(16).padStart(2, '0');
    return hex;
  }
  // Last-resort fallback (no Web Crypto at all — should never happen in a browser):
  // timestamp + Math.random. Weaker, but still per-action-unique in practice.
  return `f-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}
